import type { CSSProperties } from "react";
import type { DateStr } from "./persistence";

export type WeekDayStatus =
  | "today-checked"
  | "today"
  | "checked"
  | "missed";

export type WeekDay = {
  dayName: string;
  dayNum: number;
  status: WeekDayStatus;
  dateStr: DateStr;
};

export type WeekStripStyles = {
  weekStrip: CSSProperties;
  weekStripHeader: CSSProperties;
  weekStripLabel: CSSProperties;
  weekStripGoal: CSSProperties;
  weekDays: CSSProperties;
  weekDay: CSSProperties;
  weekDayName: CSSProperties;
  weekCircle: CSSProperties;
  weekCircleChecked: CSSProperties;
  weekCircleToday: CSSProperties;
  weekCircleTodayChecked: CSSProperties;
  weekCircleMissed: CSSProperties;
  weekCircleSelected: CSSProperties;
  missedStrike: CSSProperties;
};

type Props = {
  weekDays: WeekDay[];
  selectedDate: DateStr | null;
  onSelectDay: (date: DateStr) => void;
  goalText: string;
  styles: WeekStripStyles;
};

const buttonReset: CSSProperties = {
  border: "none",
  padding: 0,
  font: "inherit",
  cursor: "pointer",
};

function formatAriaLabel(day: WeekDay): string {
  return `Check in for ${day.dayName} ${day.dayNum}`;
}

export default function WeekStrip({
  weekDays,
  selectedDate,
  onSelectDay,
  goalText,
  styles,
}: Props) {
  return (
    <div style={styles.weekStrip}>
      <div style={styles.weekStripHeader}>
        <span style={styles.weekStripLabel}>Last 7 days</span>
        <span style={styles.weekStripGoal}>{goalText}</span>
      </div>
      <div style={styles.weekDays}>
        {weekDays.map((day, i) => {
          const isSelected = day.dateStr === selectedDate;
          let circleStyle: CSSProperties = { ...styles.weekCircle };
          if (day.status === "checked")
            circleStyle = { ...circleStyle, ...styles.weekCircleChecked };
          else if (day.status === "today")
            circleStyle = { ...circleStyle, ...styles.weekCircleToday };
          else if (day.status === "today-checked")
            circleStyle = { ...circleStyle, ...styles.weekCircleTodayChecked };
          else if (day.status === "missed") {
            circleStyle = { ...circleStyle, ...styles.weekCircleMissed };
            if (isSelected)
              circleStyle = { ...circleStyle, ...styles.weekCircleSelected };
          }

          const inner = (
            <>
              {day.dayNum}
              {day.status === "missed" && !isSelected && (
                <span style={styles.missedStrike} />
              )}
            </>
          );

          return (
            <div key={i} style={styles.weekDay}>
              <span style={styles.weekDayName}>{day.dayName}</span>
              {day.status === "missed" ? (
                <button
                  type="button"
                  style={{ ...buttonReset, ...circleStyle }}
                  onClick={() => onSelectDay(day.dateStr)}
                  aria-pressed={isSelected}
                  aria-label={formatAriaLabel(day)}
                >
                  {inner}
                </button>
              ) : (
                <div style={circleStyle}>{inner}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
